import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import { ONE_SEC_MS } from '@glass/common/modules/dates/constants';
import { selectRouteOnBlock } from '@glass/web/modules/app';
import setRouteBlockAction from '@glass/web/modules/app/setRouteBlockAction';
import setRouteUnblockAction from '@glass/web/modules/app/setRouteUnblockAction';
import useTimeout from '@glass/web/modules/dates/useTimeout';

import getScreen from '@glass/shared/modules/window/screen';

const isMouseOnScreen = ({ clientY, clientX }) => {
  const { clientWidth, clientHeight } = getScreen();
  return clientY > 0 && clientX > 0 && clientX < clientWidth && clientY < clientHeight;
};

const LONG_TIME = 999999;

export const LINK_BLOCK_EXIT_REASON = 'link-block';
export const MOUSE_EXIT_REASON = 'mouse-exit';
export const INACTIVITY_EXIT_REASON = 'inactivity';
export const SCROLL_EXIT_REASON = 'scroll';

const useDocumentEvent = (event, fn, { enabled = true, passive = true } = {}) => {
  useEffect(() => {
    if (!__BROWSER__) {
      return undefined;
    }
    if (enabled && event && fn) {
      document.documentElement.addEventListener(event, fn, { passive });
    }
    return () => document.documentElement.removeEventListener(event, fn);
  }, [enabled, event, fn, passive]);
};

const useWindowEvent = (event, fn, { enabled = true, passive = true } = {}) => {
  useEffect(() => {
    if (!__BROWSER__) {
      return undefined;
    }
    if (enabled && event && fn) {
      window.addEventListener(event, fn, { passive });
    }
    return () => window.removeEventListener(event, fn);
  }, [enabled, event, fn, passive]);
};

const useMouseLeaveIntent = ({ delay, onExit, enabled }) => {
  const handleExit = useMemo(() => onExit.bind(null, MOUSE_EXIT_REASON), [onExit]);
  const { startTimer: startMouseExitTimer, clearTimer: clearMouseExitTimer } = useTimeout(
    handleExit,
    enabled ? delay : null,
    {
      startImmediately: false,
    },
  );

  const handleMouseLeave = useCallback(
    (evt) => {
      if (!isMouseOnScreen(evt) && enabled) {
        startMouseExitTimer();
      }
    },
    [startMouseExitTimer, enabled],
  );

  useDocumentEvent('mouseleave', handleMouseLeave, { enabled });
  useDocumentEvent('mouseenter', clearMouseExitTimer, { enabled });
};

const useInactivityIntent = ({ enabled, onExit, inActivityWait }) => {
  const handleExit = useMemo(() => onExit.bind(null, INACTIVITY_EXIT_REASON), [onExit]);

  const { resetTimer: resetActivityTimer } = useTimeout(
    handleExit,
    enabled ? inActivityWait : null,
    {
      autoRestart: true,
    },
  );

  const wait = useMemo(() => Math.min(inActivityWait / 8, 500), [inActivityWait]);
  const maxWait = useMemo(() => Math.min(inActivityWait / 4, 2000), [inActivityWait]);

  const [resetActivityTimerDebounced] = useDebouncedCallback(
    resetActivityTimer,
    enabled ? wait : LONG_TIME,
    {
      maxWait,
    },
  );

  useDocumentEvent('keypress', resetActivityTimerDebounced, { enabled });
  useDocumentEvent('touchmove', resetActivityTimerDebounced, { enabled });
  useDocumentEvent('mousemove', resetActivityTimerDebounced, { enabled });
  useWindowEvent('scroll', resetActivityTimerDebounced, { enabled });
};

// only for touch devices
const useScrollIntent = ({ sensitivity, delay, enabled, onExit }) => {
  const [isTouch, setIsTouch] = useState(false);
  const isScroll = isTouch && enabled;
  const handleExit = useMemo(() => onExit.bind(null, SCROLL_EXIT_REASON), [onExit]);
  const { startTimer, clearTimer } = useTimeout(handleExit, isTouch && enabled ? delay : null, {
    startImmediately: false,
  });

  useScrollPosition(
    ({ prevPos, currPos }) => {
      if (currPos.y === 0 && -1 * prevPos.y > sensitivity) {
        if (isTouch) {
          startTimer();
        }
      } else if (currPos.y > 0) {
        clearTimer();
      }
    },
    [isTouch, startTimer, clearTimer, sensitivity],
    undefined,
    false,
    isScroll ? delay : LONG_TIME,
  );

  // detect touch events and use this hook only on touch devices
  const handleTouch = useCallback(() => setIsTouch(true), []);

  const [handleTouchDebounced] = useDebouncedCallback(handleTouch, isTouch ? LONG_TIME : delay, {
    maxWait: ONE_SEC_MS,
  });

  useDocumentEvent('touchstart', handleTouchDebounced, { enabled: !isTouch });
};

const useLinkBlock = ({ onExit, enabled }) => {
  const dispatch = useDispatch();
  const onBlock = useSelector(selectRouteOnBlock);
  const handleExit = useCallback(() => onExit(LINK_BLOCK_EXIT_REASON), [onExit]);
  useEffect(() => {
    if (enabled && onExit && onBlock !== handleExit) {
      dispatch(setRouteBlockAction({ onBlock: handleExit }));
    } else if (!enabled && onBlock) {
      dispatch(setRouteUnblockAction());
    }
  }, [dispatch, onExit, enabled, handleExit, onBlock]);
};

const useBeforeUnloadExit = ({ onExit, enabled, message }) => {
  const handleBeforeUnload = useCallback(
    (evt) => {
      onExit('beforeunload');
      // eslint-disable-next-line no-param-reassign
      evt.returnValue = message;
      return message;
    },
    [onExit, message],
  );
  useWindowEvent('beforeunload', handleBeforeUnload, { enabled: !!(enabled && message) });
};

const useExitIntent = ({
  startDelay = ONE_SEC_MS,
  delay = 100,
  maxExitCount = -1,
  onExit,
  skip: skipProp = false,
  scrollSensitivity = 200,
  inActivityWait = 30 * ONE_SEC_MS,
  unloadMessage = 'Are you sure you want to leave? You have changes that will not be saved',
  linkBlockEnable = true,
} = {}) => {
  const [started, setStarted] = useState(false);
  const [exitCount, setExitCount] = useState(0);

  const enabled = useMemo(
    () => __BROWSER__ && !skipProp && started && (maxExitCount < 0 || exitCount < maxExitCount),
    [skipProp, started, maxExitCount, exitCount],
  );

  const handleExit = useCallback(
    (reason) => {
      if (enabled) {
        setExitCount(exitCount + 1);
        onExit(reason);
      }
    },
    [enabled, exitCount, setExitCount, onExit],
  );

  const handleStart = useCallback(() => setStarted(true), []);
  useTimeout(handleStart, !started ? startDelay : null);

  useMouseLeaveIntent({ onExit: handleExit, delay, enabled });
  useScrollIntent({ onExit: handleExit, delay, enabled, sensitivity: scrollSensitivity });
  useInactivityIntent({ inActivityWait, enabled, onExit: handleExit });
  useLinkBlock({ onExit: handleExit, enabled: enabled && linkBlockEnable });
  useBeforeUnloadExit({ onExit: handleExit, enabled, message: unloadMessage });

  return { exitCount, started, enabled };
};

export default useExitIntent;
